import { useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle2, Circle, Globe, Pencil, Trash2, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import type { Lead } from '@/domains/types'
import { useAuthStore } from '@/application/stores/auth.store'
import {
  usePublishLead,
  useSetLeadConclusion,
  useUnpublishLead,
} from '@/application/services/lead.service'
import { Badge } from '@/ui/components/common/Badge'
import { ConclusionStamp } from '@/ui/components/public/ConclusionStamp'
import { MarkdownView } from '@/ui/components/common/MarkdownView'
import { cn } from '@/ui/utils/main'
import { markdownToPlainExcerpt } from '@/ui/utils/markdown'
import { EditLeadDialog } from '@/ui/components/leads/EditLeadDialog'

/**
 * Renders a single lead as a clickable tile (image/initial + title +
 * description + meta) with action buttons (edit for author/admin/streamer,
 * delete always visible on hover).
 */
export function LeadTile({
  lead,
  investigationId,
  onDelete,
  dragHandle,
}: {
  lead: Lead
  investigationId: string
  onDelete: (e: React.MouseEvent) => void
  /** Fully-wired drag handle element (icon + dnd-kit listeners), if the list is sortable. */
  dragHandle?: ReactNode
}) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const [editOpen, setEditOpen] = useState(false)

  const { mutateAsync: publishLead } = usePublishLead()
  const { mutateAsync: unpublishLead } = useUnpublishLead()
  const { mutate: setLeadConclusion, isPending: isSettingConclusion } = useSetLeadConclusion()

  const canTogglePublish = user?.role === 'admin' || user?.role === 'streamer'
  const isInvestigationAuthor =
    !!user?.id && !!lead.investigation.authorId && lead.investigation.authorId === user.id
  const canManageConclusion = canTogglePublish || isInvestigationAuthor

  const handlePublish = async () => {
    try {
      await publishLead(lead.id)
      toast.success(t('lead_detail.lead_publish_success'))
    } catch (error: unknown) {
      toast.error(
        t('lead_detail.lead_publish_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  const handleUnpublish = async () => {
    try {
      await unpublishLead(lead.id)
      toast.success(t('lead_detail.lead_unpublish_success'))
    } catch (error: unknown) {
      toast.error(
        t('lead_detail.lead_unpublish_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  const canEdit =
    !!user &&
    (user.username === lead.submitterUsername || user.role === 'admin' || user.role === 'streamer')

  const plainDescription = markdownToPlainExcerpt(lead.description)

  return (
    <>
      <div className="relative group">
        <div
          className={cn(
            'relative bg-card border rounded-lg overflow-hidden flex transition-all duration-200 group-hover:border-primary/50 group-hover:shadow-md',
            lead.published ? 'border-border' : 'border-warning/40',
          )}
        >
          {dragHandle && (
            <div className="flex items-center justify-center px-1 shrink-0 border-r border-border/50 bg-muted/40">
              {dragHandle}
            </div>
          )}
          <Link
            to="/manage/investigations/$id/leads/$leadId"
            params={{ id: investigationId, leadId: lead.id }}
            className="flex flex-1 min-w-0 no-underline hover:no-underline"
          >
            {/* Left: image, only when one exists */}
            {lead.imageUrl && (
              <div className="w-28 sm:w-40 shrink-0 overflow-hidden bg-primary/5 flex items-center justify-center">
                <img
                  src={lead.imageUrl}
                  alt={lead.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            )}

            {lead.conclusion && (
              <div className="absolute bottom-2 right-2 z-10">
                <ConclusionStamp conclusion={lead.conclusion} size={48} opaqueBackground />
              </div>
            )}

            {/* Right: content */}
            <div className="flex-1 p-4 border-l-2 border-l-primary/60 min-w-0 min-h-[100px] flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1 pr-16">
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                    {lead.title || plainDescription}
                  </h3>
                  {lead.published ? (
                    <Badge
                      variant="secondary"
                      className="text-xs shrink-0 text-success border-success/30 bg-success/10"
                    >
                      {t('lead_detail.lead_published')}
                    </Badge>
                  ) : (
                    <Badge
                      variant="secondary"
                      className="text-xs shrink-0 text-warning border-warning/30 bg-warning/10"
                    >
                      {t('lead_detail.lead_unpublished')}
                    </Badge>
                  )}
                </div>
                {lead.title && lead.description && (
                  <MarkdownView
                    content={lead.description}
                    compact
                    className="text-sm text-muted-foreground line-clamp-2"
                  />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {lead.submitterUsername} ·{' '}
                {new Date(lead.createdAt).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </p>
            </div>
          </Link>
        </div>

        {/* Action buttons (top-right overlay) */}
        <div className="absolute top-3 right-3 flex items-center gap-1 z-10">
          {canTogglePublish && !lead.published && (
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                void handlePublish()
              }}
              title={t('lead_detail.lead_publish_button')}
              className="p-1.5 rounded-lg bg-background/90 border border-border text-success hover:bg-success/10 transition-all"
            >
              <Globe className="w-3.5 h-3.5" />
            </button>
          )}
          {canTogglePublish && lead.published && (
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                void handleUnpublish()
              }}
              title={t('lead_detail.lead_unpublish_button')}
              className="p-1.5 rounded-lg bg-background/90 border border-border text-warning hover:bg-warning/10 transition-all"
            >
              <Globe className="w-3.5 h-3.5" />
            </button>
          )}
          {canManageConclusion && (
            <DropdownMenuPrimitive.Root>
              <DropdownMenuPrimitive.Trigger asChild>
                <button
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  disabled={isSettingConclusion}
                  title={t('admin.lead_conclusion_label')}
                  className="p-1.5 rounded-lg bg-background/90 border border-border transition-colors disabled:opacity-50 hover:bg-muted/50"
                >
                  {lead.conclusion === 'true' ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                  ) : lead.conclusion === 'false' ? (
                    <XCircle className="w-3.5 h-3.5 text-destructive" />
                  ) : lead.conclusion === 'fallacious' ? (
                    <AlertCircle className="w-3.5 h-3.5 text-warning" />
                  ) : lead.conclusion === 'completed' ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-info" />
                  ) : (
                    <Circle className="w-3.5 h-3.5 text-muted-foreground" />
                  )}
                </button>
              </DropdownMenuPrimitive.Trigger>
              <DropdownMenuPrimitive.Portal>
                <DropdownMenuPrimitive.Content
                  align="end"
                  sideOffset={4}
                  onClick={(event) => event.stopPropagation()}
                  className="z-50 w-44 bg-background border border-border rounded-lg shadow-lg overflow-hidden"
                >
                  {([null, 'true', 'false', 'fallacious', 'completed'] as const).map((value) => (
                    <DropdownMenuPrimitive.Item
                      key={value ?? 'none'}
                      onSelect={() => {
                        setLeadConclusion(
                          { id: lead.id, conclusion: value },
                          {
                            onSuccess: () => toast.success(t('admin.lead_conclusion_success')),
                            onError: () =>
                              toast.error(t('admin.lead_conclusion_failed', { error: '' })),
                          },
                        )
                      }}
                      className={`w-full px-3 py-2 text-xs transition-colors cursor-pointer outline-none select-none data-[highlighted]:bg-accent ${
                        lead.conclusion === value || (value === null && !lead.conclusion)
                          ? 'font-semibold text-primary'
                          : ''
                      }`}
                    >
                      {value === null
                        ? t('admin.inv_conclusion_none')
                        : value === 'true'
                          ? t('admin.inv_conclusion_true')
                          : value === 'false'
                            ? t('admin.inv_conclusion_false')
                            : value === 'fallacious'
                              ? t('admin.inv_conclusion_fallacious')
                              : t('admin.inv_conclusion_completed')}
                    </DropdownMenuPrimitive.Item>
                  ))}
                </DropdownMenuPrimitive.Content>
              </DropdownMenuPrimitive.Portal>
            </DropdownMenuPrimitive.Root>
          )}
          {canEdit && (
            <button
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setEditOpen(true)
              }}
              className="p-1.5 rounded-lg bg-background/90 border border-border text-muted-foreground hover:bg-muted/50 transition-all"
              title={t('admin.leads_edit_dialog_title')}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg bg-background/90 border border-border text-destructive hover:bg-destructive/10 transition-all"
            title={t('admin.leads_delete_confirm')}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <EditLeadDialog lead={lead} open={editOpen} onOpenChange={setEditOpen} />
    </>
  )
}
